import { useState } from 'react';
import { useRoles, useUserRoles } from '@/lib/api/hooks';
import { usePermission } from '@/lib/contexts/permission-context';
import { toast } from 'react-hot-toast';
import { User } from '@/lib/api/types';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Plus, X, Loader2 } from 'lucide-react';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';

interface UserRolesModalProps {
    user: User;
    open: boolean;
    onClose: () => void;
}

export function UserRolesModal({ user, open, onClose }: UserRolesModalProps) {
    const { roles: allRoles } = useRoles();
    const { roles: userRoles, assignRole, removeRole, isLoading, isAssigning } = useUserRoles(user.id);
    const [selectedRoleId, setSelectedRoleId] = useState<string>('');

    const availableRoles = allRoles?.filter(
        role => !userRoles?.some(ur => ur.id === role.id)
    ) || [];

    const { hasPermission } = usePermission();

    const handleAssignRole = async () => {
        if (!selectedRoleId) return;

        if (!hasPermission('users', 'manage_roles')) {
            toast.error('You do not have permission to manage roles');
            return;
        }

        try {
            await assignRole(selectedRoleId);
            setSelectedRoleId('');
            toast.success('Role assigned successfully');
        } catch (error: any) {
            toast.error(error?.response?.data?.error || 'Failed to assign role');
        }
    };

    const handleRemoveRole = async (roleId: string) => {
        if (!hasPermission('users', 'manage_roles')) {
            toast.error('You do not have permission to manage roles');
            return;
        }

        try {
            await removeRole(roleId);
            toast.success('Role removed successfully');
        } catch (error: any) {
            toast.error(error?.response?.data?.error || 'Failed to remove role');
        }
    };

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="max-w-2xl">
                <DialogHeader>
                    <DialogTitle>Manage Roles: {user.fullName}</DialogTitle>
                    <DialogDescription>{user.email}</DialogDescription>
                </DialogHeader>

                <div className="space-y-6">
                    {/* Current Roles */}
                    <div className="space-y-3">
                        <h3 className="font-semibold">Current Roles</h3>
                        {isLoading ? (
                            <p className="text-sm text-muted-foreground">Loading...</p>
                        ) : !userRoles || userRoles.length === 0 ? (
                            <Card>
                                <CardContent className="py-6 text-center">
                                    <p className="text-sm text-muted-foreground">No roles assigned</p>
                                </CardContent>
                            </Card>
                        ) : (
                            <div className="space-y-2">
                                {userRoles.map((role) => (
                                    <div
                                        key={role.id}
                                        className="flex items-center justify-between p-3 border rounded-lg"
                                    >
                                        <div className="flex items-center gap-2">
                                            <span className="font-medium">{role.name}</span>
                                            {role.isSystem && (
                                                <Badge variant="secondary" className="text-xs">
                                                    System
                                                </Badge>
                                            )}
                                        </div>
                                        {!role.isSystem && (
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => handleRemoveRole(role.id)}
                                            >
                                                <X className="h-4 w-4" />
                                            </Button>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Assign New Role */}
                    {availableRoles.length > 0 && (
                        <div className="space-y-3">
                            <h3 className="font-semibold">Assign New Role</h3>
                            <div className="flex gap-2">
                                <Select value={selectedRoleId} onValueChange={setSelectedRoleId}>
                                    <SelectTrigger className="flex-1">
                                        <SelectValue placeholder="Select a role..." />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {availableRoles.map((role) => (
                                            <SelectItem key={role.id} value={role.id}>
                                                {role.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <Button onClick={handleAssignRole} disabled={!selectedRoleId || isAssigning}>
                                    {isAssigning ? (
                                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                    ) : (
                                        <Plus className="h-4 w-4 mr-2" />
                                    )}
                                    Assign
                                </Button>
                            </div>
                        </div>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}
